import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, RefreshCw, Webhook, Mail, MessageSquare, Eye, Scale } from "lucide-react";

type Source = "all" | "whatsapp" | "email";
type Status = "all" | "processed" | "failed";

function fmtTime(d: any) {
  if (!d) return "—";
  const date = new Date(d);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }
  return date.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function sourceBadge(source: "whatsapp" | "email") {
  if (source === "whatsapp") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1">
        <MessageSquare className="w-3 h-3" /> WhatsApp
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] gap-1">
      <Mail className="w-3 h-3" /> E-mail
    </Badge>
  );
}

function statusBadge(status: string) {
  if (status === "failed") return <Badge variant="destructive" className="text-[10px]">falha</Badge>;
  if (status === "processed") return <Badge variant="secondary" className="text-[10px] bg-emerald-500/15 text-emerald-700 border-emerald-500/30">ok</Badge>;
  return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
}

/**
 * Seção de reconciliação Meta — só aparece quando um tenant específico está
 * selecionado nos filtros de cima. Permite buscar conversation_analytics da
 * Meta e comparar com nossa contagem de delivered/read em campaign_messages.
 *
 * Útil pra dimensionar webhooks de status perdidos e validar billing.
 */
// Formata divergência com sinal explícito (+/-/0).
function fmtDivergence(n: number): string {
  if (n > 0) return `+${n.toLocaleString("pt-BR")}`;
  if (n < 0) return n.toLocaleString("pt-BR"); // já vem com '-'
  return "0";
}

function ReconciliationSection({ tenantId }: { tenantId: number }) {
  const [sinceDays, setSinceDays] = useState<string>("30");
  const utils = trpc.useUtils();
  const { data: rows = [], isLoading, isFetching } = trpc.superAdmin.listMetaReconciliation.useQuery({
    tenantId,
    sinceDays: Number(sinceDays),
  });
  const runMut = trpc.superAdmin.runMetaReconciliation.useMutation({
    onSuccess: () => {
      // invalidate dispara refetch automaticamente — não precisa chamar refetch().
      utils.superAdmin.listMetaReconciliation.invalidate();
    },
  });

  const totalMeta = rows.reduce((acc, r) => acc + (r.metaConversations ?? 0), 0);
  const totalOurs = rows.reduce((acc, r) => acc + (r.ourDelivered ?? 0), 0);
  const totalDivergence = totalMeta - totalOurs;
  const totalCost = rows.reduce((acc, r) => acc + (r.metaCost ?? 0), 0);
  const showLoader = isLoading || isFetching;

  return (
    <Card className="border-dashed">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Scale className="w-4 h-4 text-primary" />
              Reconciliação Meta — Conversas pagas vs nossas entregas
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Meta cobra por <strong>conversas</strong> de 24h (não por mensagem individual). Esta tabela
              compara as conversas pagas com nossas mensagens marcadas como delivered/read — divergência
              alta indica webhooks de status perdidos historicamente.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={sinceDays} onValueChange={setSinceDays}>
              <SelectTrigger className="w-[110px] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="60">60 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="default"
              disabled={runMut.isPending}
              onClick={() => runMut.mutate({ tenantId, sinceDays: Number(sinceDays) })}
            >
              {runMut.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Sincronizar com Meta
            </Button>
          </div>
        </div>

        {runMut.error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
            {runMut.error.message}
          </div>
        )}

        {rows.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-muted/40 p-2 rounded">
              <div className="text-[10px] text-muted-foreground uppercase">Conversas (Meta)</div>
              <div className="text-lg font-semibold">{totalMeta.toLocaleString("pt-BR")}</div>
            </div>
            <div className="bg-muted/40 p-2 rounded">
              <div className="text-[10px] text-muted-foreground uppercase">Mensagens delivered (nossas)</div>
              <div className="text-lg font-semibold">{totalOurs.toLocaleString("pt-BR")}</div>
            </div>
            <div className={`p-2 rounded ${totalDivergence > 0 ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}>
              <div className="text-[10px] text-muted-foreground uppercase">Divergência</div>
              <div className={`text-lg font-semibold ${totalDivergence > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                {fmtDivergence(totalDivergence)}
              </div>
            </div>
            <div className="bg-muted/40 p-2 rounded">
              <div className="text-[10px] text-muted-foreground uppercase">Custo Meta (USD)</div>
              <div className="text-lg font-semibold">${totalCost.toFixed(2)}</div>
            </div>
          </div>
        )}

        {showLoader && rows.length === 0 ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">
            Sem dados de reconciliação ainda. Clique em "Sincronizar com Meta" para puxar.
          </p>
        ) : (
          <div className="relative">
            {showLoader && (
              <div className="absolute top-2 right-2 z-10">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Data</TableHead>
                  <TableHead className="text-right">Conversas (Meta)</TableHead>
                  <TableHead className="text-right">Delivered (nossas)</TableHead>
                  <TableHead className="text-right">Divergência</TableHead>
                  <TableHead className="text-right">Custo (USD)</TableHead>
                  <TableHead>Categorias (Meta)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const div = r.metaConversations - r.ourDelivered;
                  return (
                    <TableRow key={r.date}>
                      <TableCell className="text-xs font-mono">{r.date}</TableCell>
                      <TableCell className="text-right font-medium">{r.metaConversations.toLocaleString("pt-BR")}</TableCell>
                      <TableCell className="text-right">{r.ourDelivered.toLocaleString("pt-BR")}</TableCell>
                      <TableCell className={`text-right font-medium ${div > 0 ? "text-amber-600" : div < 0 ? "text-emerald-600" : ""}`}>
                        {fmtDivergence(div)}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        ${(r.metaCost ?? 0).toFixed(4)}
                      </TableCell>
                      <TableCell className="text-[11px] text-muted-foreground truncate max-w-[300px]">
                        {r.categories ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function WebhooksTab() {
  const [source, setSource] = useState<Source>("all");
  const [status, setStatus] = useState<Status>("all");
  const [tenantId, setTenantId] = useState<string>("all");
  const [sinceHours, setSinceHours] = useState<string>("24");
  const [limit, setLimit] = useState<string>("100");
  const [payloadOpen, setPayloadOpen] = useState<any | null>(null);

  const { data, isLoading, refetch, isFetching } = trpc.superAdmin.listWebhookEvents.useQuery({
    source,
    status,
    tenantId: tenantId === "all" ? undefined : Number(tenantId),
    sinceHours: Number(sinceHours),
    limit: Number(limit),
  });

  const rows = data?.rows ?? [];
  const summary = data?.summary;
  const tenants = data?.tenants ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <Webhook className="w-5 h-5 text-primary" />
          Webhooks
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Eventos de WhatsApp e E-mail recebidos pelo sistema. Inclui mensagens enviadas mesmo
          que ainda não tenham eventos de webhook (útil para inspecionar histórico).
        </p>
      </div>

      {/* Resumo + alerta de tenant não resolvido */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="text-2xl font-semibold">{summary.total}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">WhatsApp</div>
            <div className="text-2xl font-semibold">{summary.whatsapp}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">E-mail</div>
            <div className="text-2xl font-semibold">{summary.email}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Falhas</div>
            <div className={`text-2xl font-semibold ${summary.failed > 0 ? "text-red-600" : ""}`}>{summary.failed}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Sem tenant resolvido</div>
            <div className={`text-2xl font-semibold ${summary.unresolvedTenant > 0 ? "text-amber-600" : ""}`}>{summary.unresolvedTenant}</div>
          </CardContent></Card>
        </div>
      )}

      {summary && summary.unresolvedTenant > 0 && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm space-y-1.5">
              <div>
                <strong className="font-medium">{summary.unresolvedTenant} evento(s) sem tenant identificado.</strong>
              </div>
              <div>
                <strong>WhatsApp:</strong> <code className="text-[11px] bg-amber-100 px-1 rounded">phone_number_id</code> não
                bate com nenhum tenant cadastrado. Confirme as credenciais em <strong>Empresas → editar tenant</strong>.
              </div>
              <div>
                <strong>E-mail:</strong> <code className="text-[11px] bg-amber-100 px-1 rounded">sg_message_id</code> não casa
                com nenhuma mensagem em <code className="text-[11px] bg-amber-100 px-1 rounded">email_messages</code>. Quase
                sempre é (i) e-mail antigo enviado antes de 12/05/2026 (legado, sem ID gravado) ou (ii) evento de teste do
                dashboard SendGrid. <strong>Não é problema de credenciais</strong> — pode ignorar com segurança.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reconciliação Meta — só quando um tenant específico está selecionado */}
      {tenantId !== "all" && <ReconciliationSection tenantId={Number(tenantId)} />}

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={source} onValueChange={(v: any) => setSource(v)}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as origens</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">E-mail</SelectItem>
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={(v: any) => setStatus(v)}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="processed">Processados</SelectItem>
            <SelectItem value="failed">Falhas</SelectItem>
          </SelectContent>
        </Select>

        <Select value={tenantId} onValueChange={setTenantId}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tenants</SelectItem>
            {tenants.map((t: any) => (
              <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sinceHours} onValueChange={setSinceHours}>
          <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Última hora</SelectItem>
            <SelectItem value="24">24 horas</SelectItem>
            <SelectItem value="168">7 dias</SelectItem>
            <SelectItem value="720">30 dias</SelectItem>
          </SelectContent>
        </Select>

        <Select value={limit} onValueChange={setLimit}>
          <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="200">200</SelectItem>
            <SelectItem value="500">500</SelectItem>
          </SelectContent>
        </Select>

        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum evento de webhook nesse intervalo. Se você esperava ver dados,
              verifique a configuração do webhook no painel da Meta / SendGrid.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Quando</TableHead>
                  <TableHead className="w-[100px]">Origem</TableHead>
                  <TableHead className="w-[180px]">Tenant</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="w-[80px]">Status</TableHead>
                  <TableHead>Detalhe</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-muted-foreground">{fmtTime(r.createdAt)}</TableCell>
                    <TableCell>{sourceBadge(r.source)}</TableCell>
                    <TableCell className="text-sm">
                      {r.tenantName ?? (
                        <span className="text-amber-600 text-xs flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> não resolvido
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">{r.eventType}</code>
                      {r.kind === "message" && (
                        <Badge variant="outline" className="ml-2 text-[10px]">histórico</Badge>
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
                      {r.errorMessage ?? r.payload?.subject ?? r.payload?.to ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setPayloadOpen(r)}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal payload bruto */}
      <Dialog open={!!payloadOpen} onOpenChange={(o) => !o && setPayloadOpen(null)}>
        <DialogContent className="w-[95vw] max-w-3xl mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {payloadOpen && sourceBadge(payloadOpen.source)}
              <span className="font-mono text-sm">{payloadOpen?.eventType}</span>
              {payloadOpen && statusBadge(payloadOpen.status)}
            </DialogTitle>
          </DialogHeader>
          {payloadOpen && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Quando</div>
                  <div>{new Date(payloadOpen.createdAt).toLocaleString("pt-BR")}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Tenant</div>
                  <div>{payloadOpen.tenantName ?? <span className="text-amber-600">não resolvido</span>}</div>
                </div>
              </div>
              {payloadOpen.errorMessage && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Erro</div>
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                    {payloadOpen.errorMessage}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-muted-foreground mb-1">Payload</div>
                <pre className="text-[11px] bg-muted p-3 rounded overflow-auto max-h-[400px] whitespace-pre-wrap">
                  {JSON.stringify(payloadOpen.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
